/**
 * Tiny localStorage-backed store for prototype mock data that the user creates or
 * mutates (exam targets, wishlist, goal history, enrichment). Persisting across
 * reloads makes the prototype behave like a real backend. All keys are namespaced
 * under `bmc:mock:` so they're easy to find and clear.
 *
 * See: docs/impl-plans/registered-student-frontend-prototype.md (M0.2).
 */
const PREFIX = 'bmc:mock:';

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write<T>(key: string, value: T): void {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // storage full / unavailable — ignore in the prototype
  }
}

export interface CollectionStore<T extends { id: string }> {
  list(): T[];
  get(id: string): T | undefined;
  upsert(item: T): T;
  remove(id: string): void;
  clear(): void;
}

export function collectionStore<T extends { id: string }>(key: string): CollectionStore<T> {
  return {
    list: () => read<T[]>(key, []),
    get: (id) => read<T[]>(key, []).find((x) => x.id === id),
    upsert(item) {
      const items = read<T[]>(key, []);
      const i = items.findIndex((x) => x.id === item.id);
      if (i >= 0) items[i] = item;
      else items.push(item);
      write(key, items);
      return item;
    },
    remove(id) {
      write(
        key,
        read<T[]>(key, []).filter((x) => x.id !== id)
      );
    },
    clear() {
      write(key, [] as T[]);
    },
  };
}

export interface SingletonStore<T> {
  get(): T | null;
  set(value: T): T;
  clear(): void;
}

export function singletonStore<T>(key: string): SingletonStore<T> {
  return {
    get: () => read<T | null>(key, null),
    set(value) {
      write(key, value);
      return value;
    },
    clear() {
      write<T | null>(key, null);
    },
  };
}

/** Generate a prototype id (app code — Date/Math are fine here). */
export function mockId(prefix = 'm'): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}
